import { Component, inject, OnInit, OnDestroy, signal, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tag } from 'primeng/tag';
import { Router } from '@angular/router';
import { SortEvent } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFilterConfig,
  DynamicFilterValues,
  EnumValues,
  FilterOption,
  GlobalEntityDefinition,
  createDynamicFilterState,
} from '@card-software/library';
import { LocalHelperService } from '../../../../../services/local-helper.service';
import { EnumService } from '../../../../../services/enum.service';
import { InternalReturnProxyService } from '../../../../../proxies/inventory/internal-return.proxy.service';
import { InternalReturn, InternalReturnStatus } from '../../../../../models/inventory/internal-return';
import { InternalReturnSearchCommand } from '../../../../../models/inventory/internal-return-search';
import { DispatchRequestProxyService } from '../../../../../proxies/inventory/dispatch-request.proxy.service';
import { SupplyWarehouseDocumentType } from '../../../../../models/supply-warehouse/supply-warehouse-document-type.enum';
import { globalSortFunction } from '../../../../../utils/global-sort.util';

@Component({
  selector: 'app-internal-return-tab',
  templateUrl: './internal-return-tab.component.html',
  styleUrl: './internal-return-tab.component.scss',
})
export class InternalReturnTabComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private enumService = inject(EnumService);
  private internalReturnService = inject(InternalReturnProxyService);
  private dispatchRequestService = inject(DispatchRequestProxyService);
  public localHelperService = inject(LocalHelperService);

  private destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};

  readonly LOCATION_STORAGE_KEY = 'supply-warehouse-selected-location';
  readonly CONSUMABLES_CODE = 'NT.WH-CON-1';

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  internalReturnList = signal<InternalReturn[]>([]);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'supply-warehouse-internal-returns-filters',
    config: this.filterConfig,
    paramPrefix: 'ir.',
  });
  statusesIR = signal<EnumValues>({});
  enumsReady = signal(false);

  totalRecords = 0;
  currentPage = 1;
  pageSize = 25;

  private getTranslatedStatusOptions(): FilterOption[] {
    const statuses = this.statusesIR();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => {
        const numKey = Number(key);
        const enumName = InternalReturnStatus[numKey];
        const translationKey = `Common_Status_${enumName}`;
        const translated = this.translateService.instant(translationKey);
        return {
          value: numKey,
          label: translated !== translationKey ? translated : (statuses[numKey] as string),
        };
      });
  }

  get selectedLocation(): string {
    return localStorage.getItem(this.LOCATION_STORAGE_KEY) || this.CONSUMABLES_CODE;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusValues = this.enumService.getEnum('InternalReturnStatus', 'inventory').values;
        if (Object.keys(statusValues).length > 0) {
          this.statusesIR.set(statusValues);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.tryInitializeFilterConfig());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const t = this.translateService.instant(key);
      return t === key ? fallback : t;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('IR_Number', 'IR Number'),
          placeholder: translate('Search_IR_number', 'Search IR number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'locationCode',
          label: translate('Common_Location', 'Location'),
          placeholder: translate('Search_location', 'Search location'),
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Common_Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.getTranslatedStatusOptions(),
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'text',
          formControlName: 'dispatchRequestNumber',
          label: translate('IR_Dispatch_Number', 'Dispatch Number'),
          placeholder: translate('Search_dispatch_number', 'Search dispatch number'),
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  search(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const formValues: any = this.appliedFilters;
    const number = formValues.number?.trim();
    const locationCode = formValues.locationCode?.trim();
    const dispatchRequestNumber = formValues.dispatchRequestNumber?.trim();

    const statusesRaw = formValues.statuses;
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const baseCommand: InternalReturnSearchCommand = {
      number: number || undefined,
      locationCode: locationCode || this.selectedLocation,
      status: statuses.length === 1 ? statuses[0] : undefined,
    };

    // InternalReturn.initiator -> DispatchRequest. The API has no dispatchRequestNumber param, but it
    // does filter by `initiator`. Resolve the DR number to its id first, then filter by initiator
    // (server-side pagination/totals stay correct).
    if (dispatchRequestNumber) {
      this.isLoading.update((v) => v + 1);
      this.dispatchRequestService
        .search({ number: dispatchRequestNumber })
        .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
        .subscribe({
          next: (response) => {
            const drId = response.data?.items?.find((d) => d.number === dispatchRequestNumber)?.id ?? response.data?.items?.[0]?.id;
            if (!drId) {
              this.internalReturnList.set([]);
              this.totalRecords = 0;
              return;
            }
            this.executeSearch({
              ...baseCommand,
              initiator: { entityDefinitionCode: GlobalEntityDefinition.DispatchRequest, reference: drId },
            });
          },
          error: () => {
            this.internalReturnList.set([]);
            this.totalRecords = 0;
          },
        });
      return;
    }

    this.executeSearch(baseCommand);
  }

  private executeSearch(command: InternalReturnSearchCommand): void {
    this.isLoading.update((v) => v + 1);
    this.internalReturnService
      .search(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.internalReturnList.set(response.data?.items ?? []);
          this.totalRecords = response.data?.totalCount ?? 0;
        },
        error: () => {
          this.internalReturnList.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  onTableSort(event: SortEvent): void {
    globalSortFunction<InternalReturn>(event, ['number']);
  }

  getStatusLabel(status: InternalReturnStatus): string {
    const key = `Common_Status_${InternalReturnStatus[status]}`;
    const result = this.translateService.instant(key);
    // Rejected is IR-specific, fallback to IR_Status_Rejected
    if (result === key) {
      const irKey = `IR_Status_${InternalReturnStatus[status]}`;
      const irResult = this.translateService.instant(irKey);
      return irResult === irKey ? this.statusesIR()[status] || '' : irResult;
    }
    return result;
  }

  getStatusSeverity(status: InternalReturnStatus): Tag['severity'] {
    switch (status) {
      case InternalReturnStatus.Pending:
        return 'warn';
      case InternalReturnStatus.Processing:
        return 'info';
      case InternalReturnStatus.Completed:
        return 'success';
      case InternalReturnStatus.Cancelled:
      case InternalReturnStatus.Rejected:
        return 'danger';
      default:
        return undefined;
    }
  }

  navigateToDetail(dispatchRequestId: string): void {
    this.router.navigate(['/supply-warehouse-detail'], {
      queryParams: {
        documentType: SupplyWarehouseDocumentType.InternalReturn,
        id: dispatchRequestId,
      },
    });
  }
}
